import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, useMap, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.heat';
import api from '../services/api';

const PATRONEN = [
  { key: 'alles', label: 'Alles' },
  { key: 'vervuil', label: 'Vervuiling' },
  { key: 'demping', label: 'Demping' },
  { key: 'slijt', label: 'Slijtage' },
];

const PERIODES = [
  { key: 'week', label: 'Deze week' },
  { key: '4weken', label: 'Laatste 4 weken' },
  { key: 'alles', label: 'Alles' },
];

const WEEK_MS = 7 * 24 * 3600 * 1000;
const CAT_KLEUREN = { DRIS: '#0ea5e9', Wissel: '#00D4AA', Voedingskast: '#f59e0b', TWI: '#8b5cf6', EBS: '#ec4899' };
const catKleur = (c) => CAT_KLEUREN[c] || '#6b7280';
const VENSTER_WEKEN = 4;
const MND = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
function seizoen(m) {
  if (m === 11 || m <= 1) return { naam: 'Winter', emoji: '\u2744\ufe0f' };
  if (m <= 4) return { naam: 'Lente', emoji: '\u{1F337}' };
  if (m <= 7) return { naam: 'Zomer', emoji: '\u2600\ufe0f' };
  return { naam: 'Herfst', emoji: '\u{1F342}' };
}

function HeatLayer({ punten }) {
  const map = useMap();
  const laagRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    if (laagRef.current) { map.removeLayer(laagRef.current); laagRef.current = null; }
    if (!punten.length) return;
    const maxG = Math.max(...punten.map((p) => p.gewicht || 1));
    const data = punten.map((p) => [Number(p.lat), Number(p.lng), (p.gewicht || 1) / maxG]);
    laagRef.current = L.heatLayer(data, { radius: 40, blur: 25, maxZoom: 15, minOpacity: 0.55, max: 1.0,
      gradient: { 0.1: '#1e3a8a', 0.3: '#2563eb', 0.5: '#22c55e', 0.65: '#eab308', 0.8: '#f97316', 0.95: '#ef4444', 1.0: '#b91c1c' } });
    laagRef.current.addTo(map);
    return () => { if (laagRef.current) { map.removeLayer(laagRef.current); laagRef.current = null; } };
  }, [punten, map]);
  return null;
}

function FitEen({ punten }) {
  const map = useMap();
  const gedaan = useRef(false);
  useEffect(() => {
    if (gedaan.current || !punten.length) return;
    const geldig = punten.filter((p) => p.lat && p.lng).map((p) => [Number(p.lat), Number(p.lng)]);
    if (geldig.length) { map.fitBounds(geldig, { padding: [40, 40], maxZoom: 14 }); gedaan.current = true; }
  }, [punten, map]);
  return null;
}

export default function GeoHeatmap() {
  const navigate = useNavigate();
  const [patroon, setPatroon] = useState('alles');
  const [periode, setPeriode] = useState('week');
  const [ruw, setRuw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [afspeelModus, setAfspeelModus] = useState(false);
  const [weekIdx, setWeekIdx] = useState(0);
  const [spelen, setSpelen] = useState(false);
  const [snelheid, setSnelheid] = useState(1);
  const [objectenAan, setObjectenAan] = useState(false);
  const [objCat, setObjCat] = useState('alles');
  const [objecten, setObjecten] = useState([]);
  const [objCats, setObjCats] = useState([]);

  useEffect(() => {
    if (!objectenAan) return;
    let actief = true;
    api.get('/analyse/objecten-kaart', { params: { categorie: objCat } })
      .then((r) => { if (actief) { setObjecten(r.data.objecten || []); setObjCats(r.data.categorieen || []); } })
      .catch(() => { if (actief) setObjecten([]); });
    return () => { actief = false; };
  }, [objectenAan, objCat]);

  useEffect(() => {
    let actief = true;
    setLoading(true);
    api.get('/analyse/geo-heatmap', { params: { patroon } })
      .then((r) => { if (actief) setRuw(r.data.punten || []); })
      .catch(() => { if (actief) setRuw([]); })
      .finally(() => { if (actief) setLoading(false); });
    return () => { actief = false; };
  }, [patroon]);

  // bepaal tijdsbereik uit alle datums
  const { minMs, weken } = useMemo(() => {
    let min = Infinity, max = -Infinity;
    ruw.forEach((p) => (p.datums || []).forEach((d) => {
      const t = new Date(d).getTime();
      if (t < min) min = t; if (t > max) max = t;
    }));
    if (!isFinite(min)) return { minMs: 0, weken: 0 };
    const n = Math.max(1, Math.ceil((max - min) / WEEK_MS) + 1);
    return { minMs: min, weken: n };
  }, [ruw]);

  useEffect(() => { setWeekIdx(Math.max(0, weken - 1)); }, [weken]);

  // punten voor huidige weergave (alles, of venster bij afspelen)
  const punten = useMemo(() => {
    if (!afspeelModus) {
      if (periode === 'alles') return ruw;
      const nu = Date.now();
      let start;
      if (periode === 'week') {
        const d = new Date();
        const dag = (d.getDay() + 6) % 7; // maandag = 0
        d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - dag);
        start = d.getTime();
      } else { // '4weken'
        start = nu - 4 * WEEK_MS;
      }
      return ruw.map((p) => {
        const n = (p.datums || []).filter((dd) => { const t = new Date(dd).getTime(); return t >= start && t <= nu; }).length;
        return { ...p, gewicht: n };
      }).filter((p) => p.gewicht > 0);
    }
    const eind = minMs + (weekIdx + 1) * WEEK_MS;
    const start = eind - VENSTER_WEKEN * WEEK_MS;
    return ruw.map((p) => {
      const n = (p.datums || []).filter((d) => { const t = new Date(d).getTime(); return t >= start && t < eind; }).length;
      return { ...p, gewicht: n };
    }).filter((p) => p.gewicht > 0);
  }, [ruw, afspeelModus, weekIdx, minMs, periode]);

  // auto-play
  useEffect(() => {
    if (!spelen || !afspeelModus) return;
    const iv = setInterval(() => {
      setWeekIdx((i) => (i >= weken - 1 ? 0 : i + 1));
    }, 1200 / snelheid);
    return () => clearInterval(iv);
  }, [spelen, afspeelModus, weken, snelheid]);

  const huidigeDatum = new Date(minMs + weekIdx * WEEK_MS);
  const sz = seizoen(huidigeDatum.getMonth());
  const totaalNu = punten.reduce((s, x) => s + (x.gewicht || 0), 0);

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', padding: '20px 20px' }}>
      <button onClick={() => navigate('/analyse')} style={{ background: 'none', border: 'none', color: '#00D4AA', cursor: 'pointer', fontSize: 13, marginBottom: 8 }}>&larr; Terug naar analyse</button>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>{'\🗺️'} Geo-heatmap</h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>Waar concentreren storingen zich? Warmte = frequentie. Kies een patroon, of speel af door de tijd.</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#6b7280', marginRight: 4 }}>Periode:</span>
        {PERIODES.map((pd) => (
          <button key={pd.key} onClick={() => setPeriode(pd.key)} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
            border: '1px solid ' + (periode === pd.key ? '#0ea5e9' : '#d1d5db'),
            background: periode === pd.key ? '#0ea5e9' : '#fff',
            color: periode === pd.key ? '#fff' : '#374151' }}>{pd.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {PATRONEN.map((p) => (
          <button key={p.key} onClick={() => setPatroon(p.key)} style={{
            padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: '1px solid ' + (patroon === p.key ? '#00D4AA' : '#d1d5db'),
            background: patroon === p.key ? '#00D4AA' : '#fff',
            color: patroon === p.key ? '#fff' : '#374151' }}>{p.label}</button>
        ))}
        <button onClick={() => { setAfspeelModus((v) => !v); setSpelen(false); }} style={{
          padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginLeft: 12,
          border: '1px solid ' + (afspeelModus ? '#6366f1' : '#d1d5db'),
          background: afspeelModus ? '#6366f1' : '#fff', color: afspeelModus ? '#fff' : '#374151' }}>
          {'\u23F1\ufe0f'} Tijdlijn {afspeelModus ? 'aan' : 'uit'}</button>
        <button onClick={() => setObjectenAan((v) => !v)} style={{
          padding: '7px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          border: '1px solid ' + (objectenAan ? '#0ea5e9' : '#d1d5db'),
          background: objectenAan ? '#0ea5e9' : '#fff', color: objectenAan ? '#fff' : '#374151' }}>
          {'\u{1F4CD}'} Objecten {objectenAan ? 'aan' : 'uit'}</button>
        {objectenAan && (
          <select value={objCat} onChange={(e) => setObjCat(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}>
            <option value="alles">Alle categorie\u00EBn</option>
            {objCats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 'auto' }}>
          {loading ? 'Laden...' : punten.length + ' objecten, ' + totaalNu + ' storingen'}
        </span>
      </div>

      {afspeelModus && weken > 0 && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px' }}>
          <button onClick={() => setSpelen((v) => !v)} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontWeight: 600, minWidth: 96 }}>
            {spelen ? '\u23F8 Pauze' : '\u25B6 Afspelen'}</button>
          <input type="range" min={0} max={Math.max(0, weken - 1)} value={weekIdx} onChange={(e) => { setSpelen(false); setWeekIdx(parseInt(e.target.value, 10)); }} style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 4 }}>
            {[0.5, 1, 2].map((sp) => (
              <button key={sp} onClick={() => setSnelheid(sp)} style={{
                padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: '1px solid ' + (snelheid === sp ? '#6366f1' : '#d1d5db'),
                background: snelheid === sp ? '#6366f1' : '#fff', color: snelheid === sp ? '#fff' : '#374151' }}>{sp}×</button>
            ))}
          </div>
          <div style={{ minWidth: 220, textAlign: 'right', fontSize: 13, color: '#374151' }}>
            <strong>{sz.emoji} {sz.naam}</strong> {'\u00b7'} week van {huidigeDatum.getDate()} {MND[huidigeDatum.getMonth()]} {huidigeDatum.getFullYear()}
          </div>
        </div>
      )}

      <div style={{ height: 'calc(100vh - 260px)', minHeight: 560, borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <MapContainer key="geo-heat-map" center={[52.37, 4.89]} zoom={11} style={{ height: '100%', width: '100%' }} preferCanvas={true}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          <HeatLayer punten={punten} />
          {objectenAan && objecten.map((o) => (
            <CircleMarker key={o.id} center={[Number(o.lat), Number(o.lng)]} radius={o.open_storing ? 8 : 5}
              pathOptions={{ color: o.open_storing ? '#ef4444' : catKleur(o.categorie), fillColor: catKleur(o.categorie), fillOpacity: 0.85, weight: o.open_storing ? 3 : 1.5 }}>
              <Popup>
                <div style={{ fontSize: 13, minWidth: 180 }}>
                  <strong>{o.naam}</strong>{o.open_storing ? ' \u26A0\uFE0F' : ''}<br />
                  {[o.adres, o.plaats].filter(Boolean).join(', ')}<br />
                  <span style={{ color: '#6b7280' }}>{o.categorie}</span>
                  {o.notities ? <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 4 }}>{String(o.notities).split(' | ').slice(0, 2).join(' \u00b7 ')}</div> : null}
                  <div style={{ marginTop: 6 }}><a href={'/klanten/' + o.id} style={{ color: '#00D4AA' }}>Naar object \u2192</a></div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          <FitEen punten={ruw} />
        </MapContainer>
      </div>
    </div>
  );
}
